import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../api';
import type { VideoChapter } from '../types';
import { formatDuration } from '../utils';
import { Icon } from './Icon';

interface DraftChapter { id: string; title: string; time: string }

function parseChapterTime(value: string): number | null {
  const parts = value.trim().split(':').map(Number);
  if (!parts.length || parts.some((part) => !Number.isFinite(part) || part < 0)) return null;
  if (parts.length === 2 && parts[1]! < 60) return parts[0]! * 60 + parts[1]!;
  if (parts.length === 3 && parts[1]! < 60 && parts[2]! < 60) return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  return null;
}

function chapterTime(seconds: number): string {
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remainder = whole % 60;
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}` : `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export function ChaptersPanel({ videoId, durationSeconds, canEdit }: { videoId: string; durationSeconds: number; canEdit: boolean }) {
  const [drafts, setDrafts] = useState<DraftChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.chapters(videoId).then((chapters) => {
      if (!active) return;
      setDrafts(chapters.map((chapter) => ({ id: chapter.id, title: chapter.title, time: chapterTime(chapter.startSeconds) })));
      setDirty(false);
      setError('');
    }).catch((reason) => active && setError(reason instanceof ApiError ? reason.message : 'Unable to load chapters.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [videoId]);

  const parsed = useMemo(() => drafts.map((draft) => ({ ...draft, seconds: parseChapterTime(draft.time) })), [drafts]);
  function change(id: string, field: 'title' | 'time', value: string) {
    setDrafts((current) => current.map((draft) => draft.id === id ? { ...draft, [field]: value } : draft));
    setDirty(true);
  }
  function add() {
    const previous = parsed.at(-1)?.seconds ?? 0;
    setDrafts((current) => [...current, { id: crypto.randomUUID(), title: '', time: chapterTime(Math.min(durationSeconds - 1, previous + 60)) }]);
    setDirty(true);
  }
  async function save() {
    if (parsed.some((chapter) => !chapter.title.trim() || chapter.seconds === null || chapter.seconds >= durationSeconds)) {
      setError('Every chapter needs a title and a time inside the video duration.');
      return;
    }
    const values = parsed.map((chapter) => ({ title: chapter.title.trim(), startSeconds: chapter.seconds! }));
    if (new Set(values.map((chapter) => chapter.startSeconds)).size !== values.length) {
      setError('Chapter start times must be unique.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const saved = await api.saveChapters(videoId, values);
      setDrafts(saved.map((chapter) => ({ id: chapter.id, title: chapter.title, time: chapterTime(chapter.startSeconds) })));
      setDirty(false);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save chapters.');
    } finally { setBusy(false); }
  }

  return <section className="video-details__card chapter-panel">
    <div className="video-details__section-heading"><div><span>Navigation</span><h3>Chapters</h3></div><strong className="chapter-panel__duration">Video {formatDuration(durationSeconds)}</strong></div>
    <p className="video-details__explanation">Chapters mark ranges on the player timeline and are also published to the LMS embed bridge. They do not cut the video.</p>
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    {loading ? <div className="video-details__links-loading"><span className="spinner spinner--large" />Loading chapters…</div> : <>
      {drafts.length ? <div className="chapter-panel__list">{drafts.map((draft, index) => <div className="chapter-row" key={draft.id}>
        <span>{index + 1}</span>
        <label><span>Start</span><input aria-label={`Chapter ${index + 1} start`} value={draft.time} disabled={!canEdit} onChange={(event) => change(draft.id, 'time', event.target.value)} placeholder="0:00" /></label>
        <label><span>Title</span><input aria-label={`Chapter ${index + 1} title`} value={draft.title} disabled={!canEdit} maxLength={120} onChange={(event) => change(draft.id, 'title', event.target.value)} /></label>
        {canEdit && <button type="button" className="icon-button" aria-label={`Remove chapter ${index + 1}`} onClick={() => { setDrafts((current) => current.filter((item) => item.id !== draft.id)); setDirty(true); }}><Icon name="trash" size={16} /></button>}
      </div>)}</div> : <div className="caption-panel__empty"><Icon name="film" size={25} /><strong>No chapters</strong><span>Add navigation points for long lessons and webinars.</span></div>}
      {canEdit && <div className="chapter-panel__footer"><button type="button" className="button button--secondary" onClick={add}><Icon name="plus" size={16} />Add chapter</button><button type="button" className="button button--primary" disabled={busy || !dirty} onClick={() => void save()}>{busy ? <span className="spinner" /> : <Icon name="check" size={16} />}Save chapters</button></div>}
    </>}
  </section>;
}

export type { DraftChapter };
